/**
 * WhatsApp template registry (Chatwoot / Meta).
 * Adding a template: approve in Meta, sync Chatwoot, add one object here.
 * Do not add send-path branches for a new template name.
 */

import { OPPORTUNITY_PATH, WEME_APP_BASE_URL } from "@shared/helpers/constants";

export const WHATSAPP_PLACEHOLDER_IDS = [
  "user_name",
  "request_title",
  "date",
  "time",
  "when",
  "location",
  "seeker_details",
  "registration_link",
  "custom_message",
  "sender_name",
] as const;

export type WhatsappPlaceholderId = (typeof WHATSAPP_PLACEHOLDER_IDS)[number];

export const WHATSAPP_ADMIN_PLACEHOLDERS: readonly WhatsappPlaceholderId[] = [
  "custom_message",
];

export const WHATSAPP_PLACEHOLDER_LABELS: Record<WhatsappPlaceholderId, string> =
  {
    user_name: "שם המתנדב/ת",
    request_title: "כותרת המשימה",
    date: "תאריך",
    time: "שעה",
    when: "מועד",
    location: "מיקום",
    seeker_details: "פרטי מבקש / הגעה",
    registration_link: "קישור להרשמה",
    custom_message: "הודעה חופשית",
    sender_name: "שם השולח",
  };

export const WHATSAPP_TEMPLATE_CONTEXTS = ["event_participants"] as const;

export type WhatsappTemplateContext =
  (typeof WHATSAPP_TEMPLATE_CONTEXTS)[number];

export type WhatsappTemplateCategory = "UTILITY" | "MARKETING";

export type WhatsappTemplateDefinition = {
  key: string;
  metaName: string;
  language: string;
  category: WhatsappTemplateCategory;
  labelHe: string;
  contexts: readonly WhatsappTemplateContext[];
  bodyPlaceholders: readonly WhatsappPlaceholderId[];
  /** Approved Meta body. {{1}} matches bodyPlaceholders order. */
  bodyTemplate: string;
};

export const WHATSAPP_TEMPLATES: readonly WhatsappTemplateDefinition[] = [
  {
    key: "volunteer_invite",
    metaName: "volunteer_invite",
    language: "he",
    category: "MARKETING",
    labelHe: "הזמנה להתנדבות",
    contexts: ["event_participants"],
    bodyPlaceholders: [
      "user_name",
      "request_title",
      "when",
      "location",
      "registration_link",
    ],
    bodyTemplate: [
      "היי {{1}}, יש התנדבות חדשה שמתאימה לך בדיוק!",
      "מה עושים? {{2}}",
      "מתי זה קורה? {{3}}",
      "איפה? {{4}}",
      "מתאים לך לעשות קצת טוב? כל הפרטים והרשמה זריזה מחכים לך כאן:",
      "{{5}}",
      "אביגיל מלב אחד",
    ].join("\n"),
  },
  {
    key: "reopen_conversation",
    metaName: "reopen_conversation",
    language: "he",
    category: "UTILITY",
    labelHe: "הודעה כללית (חידוש שיחה)",
    contexts: ["event_participants"],
    bodyPlaceholders: ["user_name", "custom_message"],
    bodyTemplate: [
      "היי {{1}},",
      "לוקח לי קצת זמן לחזור אליך אבל ממש לא שכחתי.",
      "{{2}}",
      "תודה על הסבלנות. אפשר פשוט לענות לי כאן.",
      "אביגיל מלב אחד",
    ].join("\n"),
  },
  {
    key: "volunteer_details",
    metaName: "volunteer_details",
    language: "he",
    category: "UTILITY",
    labelHe: "פרטי התנדבות",
    contexts: ["event_participants"],
    bodyPlaceholders: [
      "user_name",
      "request_title",
      "when",
      "location",
      "registration_link",
    ],
    bodyTemplate: [
      "היי {{1}},",
      "הנה כל הפרטים על ההתנדבות כדי שיהיה לך נוח להירשם בהמשך:",
      "מה עושים? {{2}}",
      "מתי זה קורה? {{3}}",
      "איפה? {{4}}",
      "ברגע שמתאים לך, ההרשמה ממש כאן:",
      "{{5}}",
      "אביגיל מלב אחד",
    ].join("\n"),
  },
  {
    key: "system_links",
    metaName: "system_links",
    language: "he",
    category: "UTILITY",
    labelHe: "קישורים למערכת",
    contexts: ["event_participants"],
    bodyPlaceholders: ["user_name"],
    bodyTemplate: [
      "היי {{1}},",
      "ריכזנו הכל במקום אחד בשבילך.",
      "בקישור הבא אפשר להצטרף לקהילת המתנדבים שלנו, להעלות בקשות סיוע ולהירשם להתנדבות ביחד כקבוצה:",
      "https://www.we-me.app",
    ].join("\n"),
  },
];

export const WHATSAPP_TEMPLATE_KEYS = WHATSAPP_TEMPLATES.map((t) => t.key);

export type WhatsappPlaceholderValues = Partial<
  Record<WhatsappPlaceholderId, string>
>;

const EMPTY_PARAM = "-";

export function getWhatsappTemplate(
  key: string,
): WhatsappTemplateDefinition | undefined {
  return WHATSAPP_TEMPLATES.find((t) => t.key === key);
}

export function listWhatsappTemplatesForContext(
  context: WhatsappTemplateContext,
): WhatsappTemplateDefinition[] {
  return WHATSAPP_TEMPLATES.filter((t) => t.contexts.includes(context));
}

export function templateAdminPlaceholders(
  template: WhatsappTemplateDefinition,
): WhatsappPlaceholderId[] {
  return template.bodyPlaceholders.filter((id) =>
    (WHATSAPP_ADMIN_PLACEHOLDERS as readonly string[]).includes(id),
  );
}

export function resolveWhatsappPlaceholderValue(
  values: WhatsappPlaceholderValues,
  id: WhatsappPlaceholderId,
): string {
  const raw = values[id]?.trim();
  return raw && raw.length > 0 ? raw : EMPTY_PARAM;
}

/** Chatwoot `processed_params.body` keys are "1", "2", ... matching Meta {{1}}, {{2}}. */
export function buildWhatsappProcessedBody(
  template: WhatsappTemplateDefinition,
  values: WhatsappPlaceholderValues,
): Record<string, string> {
  const body: Record<string, string> = {};
  template.bodyPlaceholders.forEach((id, index) => {
    body[String(index + 1)] = resolveWhatsappPlaceholderValue(values, id);
  });
  return body;
}

export function buildWhatsappRegistrationLink(opportunityId: string): string {
  return `${WEME_APP_BASE_URL}${OPPORTUNITY_PATH}/${opportunityId}`;
}

/** One Meta param for "מתי זה קורה": Jerusalem date, plus time when the task has one. */
export function formatWhatsappWhen(input: {
  startDate?: Date | string | null;
  timeDescription?: string | null;
  hasTime?: boolean | null;
}): string {
  const raw = input.startDate;
  const start =
    raw instanceof Date ? raw : raw ? new Date(raw) : null;
  const hasStart = !!start && !Number.isNaN(start.getTime());
  const datePart = hasStart
    ? new Intl.DateTimeFormat("he-IL", {
        timeZone: "Asia/Jerusalem",
        weekday: "long",
        day: "2-digit",
        month: "2-digit",
      }).format(start)
    : "";
  const timeDescription = input.timeDescription?.trim() ?? "";
  if (timeDescription) {
    return datePart ? `${datePart}, ${timeDescription}` : timeDescription;
  }
  if (input.hasTime && hasStart && start) {
    const clock = new Intl.DateTimeFormat("he-IL", {
      timeZone: "Asia/Jerusalem",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(start);
    return datePart ? `${datePart} ב-${clock}` : clock;
  }
  return datePart;
}

export function renderWhatsappTranscript(
  template: WhatsappTemplateDefinition,
  values: WhatsappPlaceholderValues,
): string {
  return template.bodyPlaceholders
    .map(
      (id) =>
        `${WHATSAPP_PLACEHOLDER_LABELS[id]}: ${resolveWhatsappPlaceholderValue(values, id)}`,
    )
    .join("\n");
}

/** First name for {{1}}. Uses firstName, otherwise the first word of the full name. */
export function whatsappFirstName(input: {
  firstName?: string | null;
  name?: string | null;
}): string {
  const explicit = input.firstName?.trim();
  if (explicit) return explicit;
  const full = input.name?.trim() ?? "";
  if (!full) return "";
  return full.split(/\s+/)[0] ?? "";
}

const TASK_FILLED_PLACEHOLDERS: readonly WhatsappPlaceholderId[] = [
  "request_title",
  "when",
  "location",
  "registration_link",
];

/** Blank custom fields do not wipe values the server already filled from the task. */
export function applyWhatsappCustomFields(
  base: WhatsappPlaceholderValues,
  customFields: Record<string, string> | undefined,
  template: WhatsappTemplateDefinition,
): WhatsappPlaceholderValues {
  const next: WhatsappPlaceholderValues = { ...base };
  for (const id of template.bodyPlaceholders) {
    if (!customFields || !(id in customFields)) continue;
    const value = customFields[id]?.trim() ?? "";
    if (!value && TASK_FILLED_PLACEHOLDERS.includes(id)) continue;
    if (!value && id === "user_name") continue;
    next[id] = value;
  }
  return next;
}

/** Full WhatsApp body, same text stored on the Chatwoot message. */
export function renderWhatsappMessage(
  template: WhatsappTemplateDefinition,
  values: WhatsappPlaceholderValues,
): string {
  return template.bodyTemplate.replace(/\{\{(\d+)\}\}/g, (_match, raw: string) => {
    const id = template.bodyPlaceholders[Number(raw) - 1];
    if (!id) return "";
    return resolveWhatsappPlaceholderValue(values, id);
  });
}
